import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Scale, FileText, ChevronDown, Shield, Eye, Globe, Server, AlertTriangle, CheckCircle2, Mail, Download, BookOpen } from 'lucide-react';
interface TermSection {
  id: string;
  title: string;
  icon: React.ElementType;
  content: React.ReactNode;
}
const TERMS_SECTIONS: TermSection[] = [{
  id: 'generales',
  title: '1. Términos Generales del Servicio',
  icon: FileText,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6963">
        <p data-id="element-6964">
          Al acceder y utilizar nuestra plataforma de gestión de proyectos con
          agentes de IA (el "Servicio"), usted acepta estar sujeto a estos
          Términos y Condiciones. Si no está de acuerdo con alguna parte de los
          términos, no podrá acceder al Servicio.
        </p>
        <p data-id="element-6965">
          El Servicio está diseñado para equipos y organizaciones. Usted declara
          y garantiza que tiene la autoridad legal para vincular a su
          organización a estos términos.
        </p>
        <p data-id="element-6966">
          Nos reservamos el derecho de suspender o cancelar su cuenta si
          determinamos que ha violado estos términos o ha utilizado el Servicio
          de manera fraudulenta o ilegal.
        </p>
      </div>
}, {
  id: 'uso-aceptable',
  title: '2. Uso Aceptable',
  icon: CheckCircle2,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6967">
        <p data-id="element-6968">
          Usted se compromete a utilizar el agente de IA y las herramientas de
          la plataforma de manera responsable. Está estrictamente prohibido:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6969">
          <li data-id="element-6970">Utilizar el Servicio para fines ilegales o no autorizados.</li>
          <li data-id="element-6971">
            Intentar eludir las limitaciones de la API o los sistemas de
            facturación.
          </li>
          <li data-id="element-6972">
            Ingresar prompts o instrucciones diseñadas para comprometer la
            seguridad del agente (prompt injection).
          </li>
          <li data-id="element-6973">
            Sobrecargar intencionalmente nuestra infraestructura o la de
            nuestros proveedores integrados.
          </li>
        </ul>
      </div>
}, {
  id: 'integraciones',
  title: '3. Integraciones de Terceros',
  icon: Globe,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6974">
        <p data-id="element-6975">
          El Servicio se integra con plataformas de terceros como Google
          Workspace, Jira, GitHub y Slack mediante nuestro MCP (Model Context
          Protocol) Gateway.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6976">
          <li data-id="element-6977">
            <strong data-id="element-6978">Google Workspace:</strong> El acceso a Gmail, Calendar y
            Drive se realiza a través de OAuth. Los datos fluyen a través de
            nuestro MCP Gateway para ser procesados por el agente.
          </li>
          <li data-id="element-6979">
            <strong data-id="element-6980">Responsabilidad:</strong> No somos responsables por la
            disponibilidad o los cambios en las APIs de estos proveedores
            externos.
          </li>
          <li data-id="element-6981">
            <strong data-id="element-6982">Permisos:</strong> Usted es responsable de otorgar y revocar
            los permisos adecuados en las plataformas de terceros.
          </li>
        </ul>
      </div>
}, {
  id: 'privacidad',
  title: '4. Privacidad y Datos',
  icon: Shield,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6983">
        <p data-id="element-6984">
          La seguridad de sus datos es nuestra prioridad. Nuestro enfoque de
          arquitectura garantiza la máxima protección:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6985">
          <li data-id="element-6986">
            <strong data-id="element-6987">Almacenamiento de Tokens:</strong> Los tokens de acceso
            (OAuth) nunca se almacenan en el navegador del usuario. Se guardan
            de forma segura en AWS Secrets Manager.
          </li>
          <li data-id="element-6988">
            <strong data-id="element-6989">Procesamiento de IA:</strong> Los datos de sus integraciones
            (emails, tickets, código) se envían a los modelos de lenguaje (LLMs)
            únicamente para procesar sus consultas específicas y no se utilizan
            para entrenar modelos fundacionales.
          </li>
          <li data-id="element-6990">
            <strong data-id="element-6991">Retención:</strong> Mantenemos un historial de chat cifrado
            para la continuidad del contexto, el cual puede ser eliminado por el
            usuario en cualquier momento.
          </li>
        </ul>
      </div>
}, {
  id: 'propiedad',
  title: '5. Propiedad Intelectual',
  icon: Eye,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6992">
        <p data-id="element-6993">
          <strong data-id="element-6994">Su contenido:</strong> Usted retiene todos los derechos de
          propiedad sobre los datos, código y documentos que procesa a través de
          nuestro Servicio.
        </p>
        <p data-id="element-6995">
          <strong data-id="element-6996">Nuestra plataforma:</strong> El código fuente, diseño,
          arquitectura del agente, prompts del sistema y la marca de la
          plataforma son propiedad exclusiva de nuestra empresa y están
          protegidos por leyes de propiedad intelectual.
        </p>
      </div>
}, {
  id: 'sla',
  title: '6. Disponibilidad del Servicio (SLA)',
  icon: Server,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-6997">
        <p data-id="element-6998">
          Nos esforzamos por mantener un tiempo de actividad (uptime) del 99.9%
          para nuestros servicios principales.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-slate-400" data-id="element-6999">
          <li data-id="element-7000">
            <strong data-id="element-7001">Mantenimiento:</strong> Las ventanas de mantenimiento
            programado se notificarán con al menos 48 horas de anticipación.
          </li>
          <li data-id="element-7002">
            <strong data-id="element-7003">Dependencias:</strong> Nuestro SLA no cubre interrupciones
            causadas por caídas en los proveedores de LLM (ej. OpenAI,
            Anthropic) o APIs de terceros (Google, Atlassian).
          </li>
        </ul>
      </div>
}, {
  id: 'responsabilidad',
  title: '7. Limitación de Responsabilidad',
  icon: AlertTriangle,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-7004">
        <p data-id="element-7005">
          El Servicio, incluyendo las respuestas generadas por IA, se
          proporciona "tal cual". No garantizamos la exactitud absoluta de las
          sugerencias de código, resúmenes o acciones tomadas por el agente.
        </p>
        <p data-id="element-7006">
          En ningún caso seremos responsables por daños indirectos,
          incidentales, especiales o consecuentes, incluyendo pérdida de
          beneficios, datos o uso, que surjan de o estén relacionados con el uso
          del Servicio.
        </p>
      </div>
}, {
  id: 'modificaciones',
  title: '8. Modificaciones a los Términos',
  icon: BookOpen,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-7007">
        <p data-id="element-7008">
          Nos reservamos el derecho de modificar o reemplazar estos Términos en
          cualquier momento. Si una revisión es material, intentaremos
          proporcionar un aviso con al menos 30 días de anticipación antes de
          que los nuevos términos entren en vigencia.
        </p>
        <p data-id="element-7009">
          El uso continuo del Servicio después de que las modificaciones entren
          en vigencia constituye su aceptación de los nuevos Términos.
        </p>
      </div>
}, {
  id: 'contacto',
  title: '9. Contacto',
  icon: Mail,
  content: <div className="space-y-4 text-sm text-slate-300" data-id="element-7010">
        <p data-id="element-7011">
          Si tiene alguna pregunta sobre estos Términos y Condiciones, por favor
          contáctenos a través de:
        </p>
        <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 inline-block" data-id="element-7012">
          <p className="font-mono text-indigo-400" data-id="element-7013">legal@agent.ai</p>
        </div>
      </div>
}];
export function TermsPanel() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    generales: true
  });
  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const expandAll = () => {
    const allOpen = TERMS_SECTIONS.reduce((acc, section) => {
      acc[section.id] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenSections(allOpen);
  };
  const collapseAll = () => {
    setOpenSections({});
  };
  const scrollToSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: true
    }));
    setTimeout(() => {
      const element = document.getElementById(`term-section-${id}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-7014">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-7015">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-7016">
          <div data-id="element-7017">
            <div className="flex items-center gap-3 mb-2" data-id="element-7018">
              <div className="p-2.5 bg-indigo-950/50 rounded-xl border border-indigo-900/50" data-id="element-7019">
                <Scale size={24} className="text-indigo-400" data-id="element-7020" />
              </div>
              <h1 className="text-3xl font-bold text-slate-100" data-id="element-7021">
                Términos y Condiciones
              </h1>
            </div>
            <p className="text-slate-400 mt-2" data-id="element-7022">
              Acuerdo legal para el uso de la plataforma y agentes de IA.
            </p>
            <div className="flex items-center gap-3 mt-4" data-id="element-7023">
              <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-300" data-id="element-7024">
                Versión 2.1
              </Badge>
              <span className="text-xs text-slate-500" data-id="element-7025">
                Última actualización: 15 de Octubre, 2026
              </span>
            </div>
          </div>
          <div className="shrink-0" data-id="element-7026">
            <Button variant="outline" className="gap-2 bg-slate-900 border-slate-700 text-slate-300 hover:text-slate-100 hover:bg-slate-800" data-id="element-7027">
              <Download size={16} data-id="element-7028" />
              Descargar PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-7029">
          {/* Table of Contents (Sidebar) */}
          <div className="lg:col-span-1" data-id="element-7030">
            <div className="sticky top-8 space-y-4" data-id="element-7031">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-7032">
                Contenido
              </h3>
              <nav className="space-y-1 border-l-2 border-slate-800" data-id="element-7033">
                {TERMS_SECTIONS.map(section => <button key={`toc-${section.id}`} onClick={() => scrollToSection(section.id)} className={`w-full text-left px-4 py-2 text-sm transition-colors border-l-2 -ml-[2px] ${openSections[section.id] ? 'border-indigo-500 text-indigo-400 font-medium bg-indigo-950/10' : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-7034">
                    {section.title}
                  </button>)}
              </nav>

              <div className="pt-4 flex gap-2" data-id="element-7035">
                <button onClick={expandAll} className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-7036">
                  Expandir todo
                </button>
                <span className="text-slate-700" data-id="element-7037">•</span>
                <button onClick={collapseAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-7038">
                  Colapsar todo
                </button>
              </div>
            </div>
          </div>

          {/* Accordion Content */}
          <div className="lg:col-span-3 space-y-4 pb-20" data-id="element-7039">
            {TERMS_SECTIONS.map(section => {
            const Icon = section.icon;
            const isOpen = !!openSections[section.id];
            return <Card key={section.id} id={`term-section-${section.id}`} className={`bg-slate-900 border-slate-800 overflow-hidden transition-colors ${isOpen ? 'border-slate-700 shadow-md' : 'hover:border-slate-700'}`} data-id="element-7040">
                  <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none" data-id="element-7041">
                    <div className="flex items-center gap-3" data-id="element-7042">
                      <div className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-indigo-950/50 text-indigo-400' : 'bg-slate-800 text-slate-400'}`} data-id="element-7043">
                        <Icon size={18} data-id="element-7044" />
                      </div>
                      <h2 className={`text-lg font-semibold transition-colors ${isOpen ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-7045">
                        {section.title}
                      </h2>
                    </div>
                    <ChevronDown size={20} className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-400' : ''}`} data-id="element-7046" />
                  </button>

                  <AnimatePresence initial={false} data-id="element-7047">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} data-id="element-7048">
                        <div className="px-5 pb-5 pt-2 border-t border-slate-800/50" data-id="element-7049">
                          {section.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </div>;
}